import React, { useContext } from 'react';
import { RoleUtils } from '@galaxy/lorule-react-commons';
import { Column } from 'react-table';
import { useMsal } from '@azure/msal-react';
import ConfigContext from '../../../contexts/ConfigContext';
import { getProjectInvoiceDistributions } from '../../../api/projectBalanceApi';
import { InvoiceDistribution } from '../../../api/projectBalanceApiTypes';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { formatCurrency, renderDateCell } from '../../../utils/displayUtils';
import DataTable from '../../common/datatables/DataTable';
import Spinner from '../../common/Spinner';

const SubRowAsync = (props: {
    amId: number;
    amNumber: number;
    invoiceId: number;
    invoiceLineNumber: number;
    commitment: boolean;
}) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const { amId, invoiceId, commitment, invoiceLineNumber } = props;

    const { value: invoiceDistributions, loading } = useAsyncWithState(
        () =>
            getProjectInvoiceDistributions(
                config,
                amId,
                invoiceId,
                invoiceLineNumber,
                commitment,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            ),
        [] as InvoiceDistribution[],
    );

    const columns = React.useMemo(
        () => [
            {
                Header: 'Line #',
                accessor: 'distributionLineNumber',
                width: 75,
            },
            {
                Header: 'Accounting Date',
                accessor: 'accountingDate',
                Cell: renderDateCell,
            },
            {
                Header: 'GL Account',
                accessor: 'glAccountCombination',
            },
            {
                Header: 'Project #',
                accessor: 'projectNumber',
                width: 100,
            },
            {
                Header: 'Task #',
                accessor: 'taskNumber',
                width: 100,
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                width: 100,
                Cell: (cellProps: { value: number }) => formatCurrency(cellProps.value, 2),
            },
            {
                Header: 'Expenditure Type',
                accessor: 'expenditureType',
            },
            {
                Header: 'Expenditure Item Date',
                accessor: 'expenditureItemDate',
                Cell: renderDateCell,
            },
        ],
        [],
    );

    return !loading ? (
        <div>
            {invoiceDistributions && invoiceDistributions.length > 0 && (
                <div className="react-table">
                    <h5 className="pt-3 mb-2">Distributions</h5>
                    <DataTable
                        columns={columns as Column<Object>[]}
                        data={invoiceDistributions}
                        noGlobalFilter
                        noPagination
                        initialState={{ pageSize: 1000 }}
                    />
                </div>
            )}
        </div>
    ) : (
        <Spinner />
    );
};

export const generateProjectInvoiceDistributionsSubRow = (
    amId: number,
    amNumber: number,
    invoiceId: number,
    commitment: boolean,
) =>
    React.useCallback(
        ({ row }: { row: { original: { lineNumber: number } } }) => (
            <SubRowAsync
                amId={amId}
                amNumber={amNumber}
                invoiceId={invoiceId}
                commitment={commitment}
                invoiceLineNumber={row.original.lineNumber}
            />
        ),
        [],
    );
